import { Search } from 'lucide-react'
import type { HelpCenterSettings, HelpCenterTheme } from '@/pages/HelpCenter/types'

const RADIUS: Record<string, string> = { sharp: '2px', default: '10px', rounded: '20px' }
const FONT: Record<string, string> = {
  sans: 'ui-sans-serif, system-ui, sans-serif',
  serif: 'ui-serif, Georgia, serif',
  mono: 'ui-monospace, SFMono-Regular, monospace',
}

interface HelpThemePreviewProps {
  settings: HelpCenterSettings
  theme: HelpCenterTheme
  // Product brand primary/accent — the preview inherits them when the theme
  // hasn't overridden (matching the public renderer HelpCenterTheme#primary).
  brandPrimary?: string | null
  brandAccent?: string | null
  heroHeading: string
  heroSubtext: string
}

const TEMPLATE_LABEL: Record<string, string> = { classic: 'Classic', aurora: 'Aurora', atlas: 'Atlas' }

// Self-contained preview (inline styles) so it reflects the account theme, not
// the dashboard theme.
export function HelpThemePreview({ settings, theme, brandPrimary, brandAccent, heroHeading, heroSubtext }: HelpThemePreviewProps) {
  const primary = theme.primary_color ?? brandPrimary ?? '#10b981'
  const accent = theme.accent_color ?? brandAccent ?? primary
  const template = theme.template ?? 'classic'
  const light = theme.appearance === 'light'
  const radius = RADIUS[theme.radius ?? 'default']
  const fontFamily = FONT[theme.font ?? 'sans']

  // Structural overrides win; otherwise fall back to the default light/dark set.
  const bg = theme.bg_color || (light ? '#f6f8fb' : '#0b0f14')
  const page = theme.page_color || bg
  const card = theme.surface_color || (light ? '#ffffff' : '#141a22')
  const text = theme.text_color || (light ? '#1a2330' : '#e7ebf0')
  const muted = theme.muted_color || (light ? '#5b6776' : '#93a1b2')
  const border = theme.border_color || (light ? '#e3e8ef' : '#232c38')

  const categories = ['Getting started', 'Billing', 'Integrations']

  const heroText = heroHeading || 'How can we help?'
  const heroSub = heroSubtext || 'Search our guides, or browse by category.'

  const brandMark = settings.logo_url
    ? <img src={settings.logo_url} alt="logo" style={{ height: 22, objectFit: 'contain' }} />
    : <div style={{ width: 22, height: 22, borderRadius: radius, background: primary }} />

  const searchBar = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: card, border: `1px solid ${border}`, borderRadius: radius, padding: '8px 10px' }}>
      <Search size={14} color={muted} />
      <span style={{ fontSize: 12, color: muted }}>Search…</span>
    </div>
  )

  const catCard = (name: string, span = 1) => (
    <div key={name} style={{ gridColumn: `span ${span}`, background: card, border: `1px solid ${border}`, borderRadius: radius, padding: 10 }}>
      <div style={{ width: 18, height: 18, borderRadius: radius, background: primary, opacity: 0.85, marginBottom: 8 }} />
      <div style={{ fontSize: 12, fontWeight: 600 }}>{name}</div>
      <div style={{ fontSize: 10.5, color: muted, marginTop: 2 }}>12 articles</div>
    </div>
  )

  return (
    <div className="overflow-hidden rounded-lg border border-border">
      <div className="border-b border-border bg-muted/40 px-3 py-1.5 text-xs text-muted-foreground">
        Preview · <span className="font-medium text-foreground">{TEMPLATE_LABEL[template]}</span>
      </div>
      <div style={{ background: bg, padding: '0 16px' }}>
        <div style={{ background: page, color: text, fontFamily, maxWidth: 460, margin: '0 auto' }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '16px 12px 0' }}>
            {brandMark}
            <span style={{ fontWeight: 600, fontSize: 14 }}>Help center</span>
          </div>

          {template === 'aurora' && (
            <div style={{ padding: 12 }}>
              <div style={{ textAlign: 'center', padding: '22px 8px', borderRadius: radius, background: `radial-gradient(60% 90% at 15% 20%, ${primary}, transparent 70%), radial-gradient(60% 90% at 90% 10%, ${accent}, transparent 70%), ${bg}` }}>
                <div style={{ fontSize: 18, fontWeight: 700 }}>{heroText}</div>
                <div style={{ fontSize: 11.5, color: text, opacity: 0.7, margin: '4px 0 12px' }}>{heroSub}</div>
                <div style={{ maxWidth: 300, margin: '0 auto' }}>{searchBar}</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginTop: 12 }}>
                {catCard('Engage with features', 2)}
                {catCard('Getting started', 1)}
                {catCard('Integrations', 2)}
                {catCard('Billing', 1)}
              </div>
            </div>
          )}

          {template === 'atlas' && (
            <div style={{ display: 'grid', gridTemplateColumns: '96px 1fr', gap: 12, padding: 12 }}>
              <div style={{ borderRight: `1px solid ${border}`, paddingRight: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {['Getting started', 'Integrations', 'Billing', 'Security'].map((n, i) => (
                  <div key={n}>
                    <div style={{ fontSize: 10.5, fontWeight: 700, color: i === 0 ? primary : text }}>{n}</div>
                    <div style={{ fontSize: 9.5, color: muted, paddingLeft: 6, borderLeft: `1px solid ${border}`, marginTop: 3 }}>Article one<br />Article two</div>
                  </div>
                ))}
              </div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700 }}>{heroText}</div>
                <div style={{ fontSize: 11, color: muted, margin: '3px 0 10px' }}>{heroSub}</div>
                {searchBar}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 12 }}>
                  {catCard('Popular guide')}
                  {catCard('Popular guide')}
                </div>
              </div>
            </div>
          )}

          {template === 'classic' && (
            <div style={{ padding: 12 }}>
              <div style={{ textAlign: 'center', padding: '8px 0 18px' }}>
                <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>{heroText}</div>
                <div style={{ fontSize: 12.5, color: muted, marginBottom: 14 }}>{heroSub}</div>
                <div style={{ display: 'flex', gap: 8, maxWidth: 360, margin: '0 auto' }}>
                  <div style={{ flex: 1 }}>{searchBar}</div>
                  <div style={{ background: primary, color: light ? '#fff' : '#06231a', borderRadius: radius, padding: '8px 14px', fontSize: 12, fontWeight: 600 }}>Search</div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                {categories.map(name => catCard(name))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
